import { ref, computed, watch } from 'vue'
import { useActiveReport } from './useActiveReport'
import { DEPT_TO_SEGMENT, REPORT_TO_SUBSEGMENT, OVERVIEW_SUBSEGMENT } from '../data/workflowMapping'

const promptData = ref([])
const editedPrompts = ref({})        // keyed by index, stores edited text per row
const isLoading = ref(false)
const isSaving = ref(false)
const isImplementing = ref(false)
const loadError = ref(null)
const saveStatus = ref(null)         // null | 'success' | 'error'
const saveMessage = ref('')
const implementStatus = ref(null)
const implementMessage = ref('')

let _initialized = false

function ensureInitialized() {
  if (_initialized) return
  _initialized = true

  const { reportContextKey } = useActiveReport()

  watch(reportContextKey, () => {
    fetchPrompt()
    saveStatus.value = null
    saveMessage.value = ''
    implementStatus.value = null
    implementMessage.value = ''
  }, { immediate: true })
}

function getCurrentMapping() {
  const { currentDept, currentReport, isOverview } = useActiveReport()
  const segment = DEPT_TO_SEGMENT[currentDept.value?.id]
  if (!segment) return null

  let subSegment
  if (isOverview.value) {
    subSegment = OVERVIEW_SUBSEGMENT
  } else if (currentReport.value) {
    subSegment = REPORT_TO_SUBSEGMENT[currentReport.value.id]
  }
  if (!subSegment) return null
  return { segment, subSegment }
}

async function fetchPrompt() {
  const mapping = getCurrentMapping()
  if (!mapping) {
    promptData.value = []
    editedPrompts.value = {}
    loadError.value = null
    return
  }

  isLoading.value = true
  loadError.value = null

  try {
    const params = new URLSearchParams({
      segment: mapping.segment,
      subSegment: mapping.subSegment,
    })
    const resp = await fetch(`/api/prompt?${params}`)
    const data = await resp.json()

    if (resp.ok && data.success) {
      promptData.value = data.data || []
      // Initialize edited text per row
      const edited = {}
      promptData.value.forEach((row, i) => {
        edited[i] = row.mainprompt || ''
      })
      editedPrompts.value = edited
    } else {
      promptData.value = []
      editedPrompts.value = {}
      if (resp.status !== 404) {
        loadError.value = data.message || 'Failed to load prompt'
      }
    }
  } catch (err) {
    loadError.value = err.message
    promptData.value = []
    editedPrompts.value = {}
  } finally {
    isLoading.value = false
  }
}

async function savePrompt() {
  const mapping = getCurrentMapping()
  if (!mapping) return

  isSaving.value = true
  saveStatus.value = null
  saveMessage.value = ''

  try {
    // Save each edited prompt row
    for (let i = 0; i < promptData.value.length; i++) {
      const original = promptData.value[i].mainprompt || ''
      const edited = editedPrompts.value[i] || ''
      if (edited === original) continue

      const resp = await fetch('/api/prompt', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          segment: mapping.segment,
          subSegment: mapping.subSegment,
          mainPrompt: edited,
        }),
      })
      const data = await resp.json()
      if (!resp.ok || !data.success) {
        saveStatus.value = 'error'
        saveMessage.value = data.message || 'Gagal menyimpan prompt'
        isSaving.value = false
        return
      }
    }

    // Refresh data after save
    await fetchPrompt()
    saveStatus.value = 'success'
    saveMessage.value = 'Prompt berhasil disimpan'
  } catch (err) {
    saveStatus.value = 'error'
    saveMessage.value = err.message
  } finally {
    isSaving.value = false
    if (saveStatus.value === 'success') {
      setTimeout(() => { saveStatus.value = null; saveMessage.value = '' }, 3000)
    }
  }
}

async function implementPrompt() {
  const mapping = getCurrentMapping()
  if (!mapping) return

  isImplementing.value = true
  implementStatus.value = null
  implementMessage.value = ''

  try {
    const resp = await fetch('/api/prompt/implement', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        segment: mapping.segment,
        subSegment: mapping.subSegment,
      }),
    })
    const data = await resp.json()

    if (resp.ok && data.success) {
      implementStatus.value = 'success'
      implementMessage.value = `Implementasi berhasil (${data.rowsAffected} row${data.rowsAffected !== 1 ? 's' : ''} affected)`
    } else {
      implementStatus.value = 'error'
      implementMessage.value = data.message || 'Gagal implementasi'
    }
  } catch (err) {
    implementStatus.value = 'error'
    implementMessage.value = err.message
  } finally {
    isImplementing.value = false
    if (implementStatus.value === 'success') {
      setTimeout(() => { implementStatus.value = null; implementMessage.value = '' }, 3000)
    }
  }
}

const isDirty = computed(() => {
  for (let i = 0; i < promptData.value.length; i++) {
    const original = promptData.value[i].mainprompt || ''
    const edited = editedPrompts.value[i] || ''
    if (edited !== original) return true
  }
  return false
})

const currentPromptLabel = computed(() => {
  const mapping = getCurrentMapping()
  if (!mapping) return ''
  return `${mapping.segment} / ${mapping.subSegment}`
})

export function usePromptData() {
  ensureInitialized()

  return {
    promptData,
    editedPrompts,
    isLoading,
    isSaving,
    isImplementing,
    loadError,
    saveStatus,
    saveMessage,
    implementStatus,
    implementMessage,
    isDirty,
    currentPromptLabel,
    fetchPrompt,
    savePrompt,
    implementPrompt,
  }
}
